import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ApiService } from '../../../services/api.service';
import { Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-vendor-management',
  standalone: false,
  templateUrl: './vendor-management.component.html',
  styleUrls: ['./vendor-management.component.scss']
})
export class VendorManagementComponent implements OnInit, OnDestroy {
  @Input() stepDetails: any;
  @Input() schemeType: string = 'rev';
  @Input() format: string = 'upload';
  @Input() url: string = '';
  @Input() proposalStepId: any;
  @Input() proposalId: any;

  @Output() vendorAdded = new EventEmitter<any>();
  @Output() vendorUpdated = new EventEmitter<any>();
  @Output() vendorDeleted = new EventEmitter<any>();
  @Output() filesSubmitted = new EventEmitter<any>();

  visible: boolean = false;
  commentForm: FormGroup;
  selectedVendor: any;
  selectedMasterVendor: any;
  masterVendorList: any[] = [];
  winners: any[] = [];
  resId: any;
  selectedvendor: any;

  files = {
    'file1': null as File | null,
    'file2': null as File | null,
  };

  private subscriptions = new Subscription();
  apiUrl = environment.APIUrl;

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private api: ApiService
  ) {
    this.commentForm = this.fb.group({
      vendor: ['', Validators.required],
      contactPerson: ['', [Validators.required, Validators.pattern(/^[A-Za-z\s]+$/)]],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      address: ['', [Validators.required, Validators.pattern('^[a-zA-Z0-9 ,.()&-]+$')]],
      taxNumber: ['', [Validators.required, Validators.pattern('^[a-zA-Z0-9 ,.()&-]+$')]],
      bankDetails: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    if (this.format === 'upload' && this.stepDetails?.can_edit) {
      this.getVendorList();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getVendorList(): void {
    const sub = this.api.getAPI(`/master/vendor/`).subscribe((res: any) => {
      this.masterVendorList = res.data || [];
    });
    this.subscriptions.add(sub);
    this.getVendorIdentification();
  }

  getVendorIdentification(): void {
    let url;
    if (this.schemeType == 'idex') {
      url = `/idex/vendor-identification/?proposal_step__proposal=${this.proposalId}&proposal_step=${this.proposalStepId}`;
    } else if (this.schemeType == 'tdf') {
      url = `/tdf/vendor-identification/?proposal_step__proposal=${this.proposalId}&proposal_step=${this.proposalStepId}`;
    } else if (this.schemeType == 'makeI' || this.schemeType == 'makeII' || this.schemeType == 'makeIII') {
      url = `/make1/vendor-identification/?proposal_step__proposal=${this.proposalId}&proposal_step=${this.proposalStepId}`;
    } else {
      url = `/revenue/vendor-identification/?proposal_step__proposal=${this.proposalId}&proposal_step=${this.proposalStepId}`;
    }

    const sub = this.api.getAPI(url).subscribe((res: any) => {
      this.resId = res?.id;
      const vendorMap = res?.vender || res?.vendor || {};
      this.winners = Object.values(vendorMap).flat();
      this.winners.forEach((vendor: any) => {
        vendor.isSectionOpen = false;
      });
    });
    this.subscriptions.add(sub);
  }

  addVendorFromMaster(): void {
    if (!this.selectedMasterVendor || !this.selectedMasterVendor.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select at least one vendor from the master list.'
      });
      return;
    }

    // Check for duplicates
    const duplicateVendors: string[] = [];
    this.winners?.forEach((vendor: any) => {
      if (this.selectedMasterVendor?.includes(vendor?.vendor?.id)) {
        duplicateVendors.push(vendor.vendor?.name || 'Unknown');
      }
    });

    if (duplicateVendors.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Duplicate',
        detail: `${duplicateVendors.join(', ')} already added.`
      });
      return;
    }

    let payload: any;
    if (this.schemeType == 'tdf') {
      payload = {
        proposal_step: this.proposalStepId,
        step: this.resId,
        vendor_id: this.selectedMasterVendor,
        status: 1
      };
    } else if (this.schemeType == 'idex') {
      payload = {
        step: this.resId,
        vendor_id: this.selectedMasterVendor,
        status: 1
      };
    } else {
      payload = {
        step: this.resId,
        vendor_id: this.selectedMasterVendor,
        status: 1
      };
    }

    let url: string;
    if (this.schemeType == 'idex') {
      url = `/idex/vendor-identification/`;
    } else if (this.schemeType == 'tdf') {
      url = `/tdf/vendor-identification/`;
    } else if (this.schemeType == 'makeI' || this.schemeType == 'makeII' || this.schemeType == 'makeIII') {
      url = `/make1/vendor-identification/`;
    } else {
      url = `/revenue/vendor-identification/`;
    }

    const sub = this.api.postAPI(url, payload).subscribe({
      next: (res: any) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Vendor(s) added successfully.'
        });
        this.selectedMasterVendor = null;
        this.getVendorIdentification();
        this.vendorAdded.emit(res);
      },
      error: (err: any) => {
        console.error('Error adding vendor from master:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.message || 'Failed to add vendor from master. Please try again.'
        });
      }
    });
    this.subscriptions.add(sub);
  }

  openAdd(): void {
    this.visible = true;
    this.commentForm.reset();
    this.selectedvendor = null;
  }

  onSubmit(): void {
    if (this.commentForm.invalid) {
      this.commentForm.markAllAsTouched();
      return;
    }

    let payload: any;
    let url: string;

    if (this.schemeType == 'tdf') {
      payload = {
        step: this.resId,
        proposal_step: this.proposalStepId,
        company_name: this.commentForm.get('vendor')?.value,
        email: this.commentForm.get('email')?.value,
        contact: this.commentForm.get('contactPerson')?.value,
        phone_number: this.commentForm.get('phone')?.value,
        address: this.commentForm.get('address')?.value,
        tax_number: this.commentForm.get('taxNumber')?.value,
        bank_details: this.commentForm.get('bankDetails')?.value,
        status: 1
      };
      url = `/tdf/vendor-identification/`;
    } else if (this.schemeType != 'makeI' && this.schemeType != 'makeII' && this.schemeType != 'makeIII') {
      payload = {
        proposal_step: this.proposalStepId,
        company_name: this.commentForm.get('vendor')?.value,
        email: this.commentForm.get('email')?.value,
        contact: this.commentForm.get('contactPerson')?.value,
        phone_number: this.commentForm.get('phone')?.value,
        address: this.commentForm.get('address')?.value,
        tax_number: this.commentForm.get('taxNumber')?.value,
        bank_details: this.commentForm.get('bankDetails')?.value,
        status: 1
      };
      url = `/revenue/add/vendor/`;
    }

    if (this.selectedvendor) {
      // Update existing vendor
      const sub = this.api.putAPI(url + `${this.selectedvendor.id}/`, payload).subscribe({
        next: (res: any) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Vendor updated successfully'
          });
          this.visible = false;
          this.commentForm.reset();
          this.selectedvendor = null;
          this.getVendorIdentification();
          this.vendorUpdated.emit(res);
        },
        error: (err: any) => {
          console.error('Error updating vendor:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update vendor. Please try again.'
          });
        }
      });
      this.subscriptions.add(sub);
    } else if (this.schemeType != 'makeI' && this.schemeType != 'makeII' && this.schemeType != 'makeIII') {
      // Create new vendor
      const sub = this.api.postAPI(url, payload).subscribe({
        next: (res: any) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Vendor added successfully'
          });
          this.visible = false;
          this.commentForm.reset();
          this.getVendorIdentification();
          this.vendorAdded.emit(res);
        },
        error: (err: any) => {
          console.error('Error adding vendor:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add vendor. Please try again.'
          });
        }
      });
      this.subscriptions.add(sub);
    }
  }

  onEdit(vendor: any): void {
    this.visible = true;
    this.selectedvendor = vendor.vendor || vendor;
    this.commentForm.patchValue({
      vendor: this.selectedvendor.name || this.selectedvendor.company_name,
      contactPerson: this.selectedvendor.contact,
      email: this.selectedvendor.email,
      phone: this.selectedvendor.phone_number,
      address: this.selectedvendor.address,
      taxNumber: this.selectedvendor.tax_number,
      bankDetails: this.selectedvendor.bank_details
    });
  }

  removeVendor(vendor: any): void {
    let url: string;
    if (this.schemeType == 'idex') {
      url = `/idex/vendor-identification/${vendor.id}/`;
    } else if (this.schemeType == 'tdf') {
      url = `/tdf/vendor-identification/${vendor.id}/`;
    } else if (this.schemeType == 'makeI' || this.schemeType == 'makeII' || this.schemeType == 'makeIII') {
      url = `/make1/vendor-identification/${vendor.id}/`;
    } else {
      url = `/revenue/vendor-identification/${vendor.id}/`;
    }

    const sub = this.api.deleteAPI(url).subscribe({
      next: (res: any) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Vendor deleted successfully'
        });
        this.getVendorIdentification();
        this.vendorDeleted.emit(vendor);
      },
      error: (err: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to delete vendor. Please try again.'
        });
      }
    });
    this.subscriptions.add(sub);
  }

  handleFileInput(event: any, fileName: string): void {
    const file = event.target.files[0];
    if (file) {
      this.files[fileName] = file;
    }
  }

  submitFiles(): void {
    if (!this.files.file1 || !this.files.file2) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please upload both CSV and ZIP files.'
      });
      return;
    }

    const formData = new FormData();
    formData.append('step', this.resId);
    formData.append('vendor_file', this.files.file1);
    formData.append('briefcase_file', this.files.file2);

    let url: string;
    if (this.schemeType == 'idex') {
      url = `/idex/vendor-identification/`;
    } else if (this.schemeType == 'tdf') {
      url = `/tdf/vendor-identification/`;
    } else if (this.schemeType == 'makeI' || this.schemeType == 'makeII' || this.schemeType == 'makeIII') {
      url = `/make1/vendor-identification/`;
    } else {
      url = `/revenue/vendor-identification/`;
    }

    const sub = this.api.postAPI(url, formData).subscribe({
      next: (res: any) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Files uploaded successfully'
        });
        this.files.file1 = null;
        this.files.file2 = null;
        // Reset file inputs
        const fileInput1 = document.getElementById('fileUpload1') as HTMLInputElement;
        const fileInput2 = document.getElementById('fileUpload2') as HTMLInputElement;
        if (fileInput1) fileInput1.value = '';
        if (fileInput2) fileInput2.value = '';
        this.getVendorIdentification();
        this.filesSubmitted.emit(res);
      },
      error: (err: any) => {
        console.error('Error uploading files:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.message || 'Failed to upload files. Please try again.'
        });
        if (err?.error_file_url) {
          const errorFileUrl = `${this.apiUrl}${err.error_file_url}`;
          const link = document.createElement('a');
          link.href = errorFileUrl;
          const fileName = errorFileUrl.split('/').pop() || 'error_file.csv';
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    });
    this.subscriptions.add(sub);
  }

  allowOnlyNumbers(event: KeyboardEvent): void {
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
    if (allowedKeys.includes(event.key)) return;

    const isNumber = /^[0-9]$/.test(event.key);
    if (!isNumber) {
      event.preventDefault();
    }
  }

  toggleVendorSection(vendor: any): void {
    vendor.isSectionOpen = !vendor.isSectionOpen;
  }

  setStatus(vendor: any, status: 'Qualify' | 'Disqualify'): void {
    vendor.vendor_status = status;
    const formData = new FormData();
    formData.append('vendor_status', vendor.vendor_status);
    formData.append('vendor_id', vendor.id);

    let url: string;
    if (this.schemeType === 'tdf') {
      url = `/tdf/evaluation/tpr/`;
    } else if (this.schemeType == 'makeI' || this.schemeType == 'makeII' || this.schemeType == 'makeIII') {
      url = `/make1/eol-documents/`;
    } else if (this.schemeType == 'idex' && this.format == 'evaluation') {
      url = `/idex/evaluation/`;
    } else if (this.schemeType == 'idex' && this.format == 'screening') {
      url = `/idex/vendor-identification/`;
    } else {
      url = `/revenue/upload/dpr/`;
    }

    const sub = this.api.putAPI(url, formData).subscribe({
      next: (res: any) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: res.message || 'Vendor status updated successfully'
        });
        this.getVendorIdentification();
      },
      error: (err: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update vendor status. Please try again.'
        });
      }
    });
    this.subscriptions.add(sub);
  }
}

